import { GetStaticPaths, GetStaticProps } from 'next'
import Masonry from 'react-masonry-css'
import { useRef } from 'react'
import { NextSeo } from 'next-seo'

import { FaqSearchSection } from 'sections/faqs'
import { Faq, FaqCategory, strapi } from 'api'
import { BackendError } from 'api/strapi/BackendError'
import { REVALIDATION_ERROR_TIMEOUT, REVALIDATION_TIMEOUT } from 'const/config'
import { LinkButton } from 'components/links'
import { routes } from 'utils/routes'
import { CTASection } from 'sections/common'
import { FaqSubCategory, FaqTrendingSection } from 'components/faqs'
import { Section } from 'components/section'
import { FaqCategoryInfo } from 'components/faqs/faq-category-info'

import classes from './categories.module.css'

interface FaqProps {
  faqCategory: FaqCategory
  trendingItems: Faq[]
}

const sortByFaqsNumber = (unsorted: Record<string, Faq[]>) => {
  return Object.keys(unsorted)
    .sort((a, b) => {
      const aLength = unsorted[a]?.length ?? 0
      const bLength = unsorted[b]?.length ?? 0

      return bLength - aLength
    })
    .reduce((obj, key) => {
      obj[key] = unsorted[key]
      return obj
    }, {})
}

const groupBySubCategory = (faqs?: Faq[]) => {
  const FAQS_WITHOUT_CATEGORY_NAME = 'Other'
  const fqsBySubCategory =
    faqs?.reduce(function (result, faq) {
      const subCategory = faq.faq_sub_category?.title ?? FAQS_WITHOUT_CATEGORY_NAME
      ;(result[subCategory] = result[subCategory] || []).push(faq)

      return result
    }, {}) ?? {}

  const faqsWithoutCategory = fqsBySubCategory[FAQS_WITHOUT_CATEGORY_NAME]
  delete fqsBySubCategory[FAQS_WITHOUT_CATEGORY_NAME]

  return sortByFaqsNumber({
    ...fqsBySubCategory,
    ...(faqsWithoutCategory && { [FAQS_WITHOUT_CATEGORY_NAME]: faqsWithoutCategory }),
  })
}

export default function FaqCategoryPage({ faqCategory, trendingItems }: FaqProps) {
  const { current: fqsBySubCategory } = useRef(groupBySubCategory(faqCategory.faqs))

  const categoriesNumber = Object.keys(fqsBySubCategory).length
  const isOnlyFaqsWithoutSubCategory = categoriesNumber === 1

  return (
    <>
      <NextSeo title={`${faqCategory.title} FAQ Category`} />

      <FaqSearchSection />
      <FaqTrendingSection items={trendingItems} />
      <Section>
        <div className={classes.content}>
          <header className={classes.header}>
            <FaqCategoryInfo item={faqCategory} />

            <LinkButton theme="goBack" href={routes.faqs} className={classes.backButton}>
              Back To All Topics
            </LinkButton>
          </header>

          {isOnlyFaqsWithoutSubCategory ? (
            <>
              {Object.keys(fqsBySubCategory).map((subCategory, index) => {
                const faqs = fqsBySubCategory[subCategory]!

                return <FaqSubCategory faqs={faqs} className={classes.faqsContainer} key={index} />
              })}
            </>
          ) : (
            <Masonry
              breakpointCols={{ default: Math.min(categoriesNumber, 3), 1000: 2, 767: 1 }}
              className={classes.faqsMasonryContainer!}
              columnClassName={classes.faqsMasonryItem}
            >
              {Object.keys(fqsBySubCategory).map((subCategory) => {
                const faqs = fqsBySubCategory[subCategory]!

                return <FaqSubCategory faqs={faqs} name={subCategory} key={subCategory} />
              })}
            </Masonry>
          )}
        </div>
      </Section>
      <CTASection />
    </>
  )
}

export const getStaticProps: GetStaticProps<FaqProps> = async (context) => {
  const { slug } = context.params ?? {}

  if (!slug || Array.isArray(slug)) {
    return {
      notFound: true,
      revalidate: REVALIDATION_ERROR_TIMEOUT,
    }
  }

  try {
    const [faqCategory, trendingItems] = await Promise.all([
      strapi.faqCategories.find({
        filters: {
          slug: { $eq: slug },
        },
        populate: {
          faqs: {
            populate: ['faq_sub_category'],
          },
          icon: '*',
        },
      }),
      strapi.faqs.trendingItems(),
    ])

    if (!faqCategory.items[0]) {
      return {
        notFound: true,
        revalidate: REVALIDATION_ERROR_TIMEOUT,
      }
    }

    const firtsItem = faqCategory.items[0]

    const formattedFaqCategory = {
      ...firtsItem,
      faqs: firtsItem.faqs?.filter((el) => el.publishedAt),
    }

    return {
      revalidate: REVALIDATION_TIMEOUT,
      props: {
        faqCategory: formattedFaqCategory,
        trendingItems,
      },
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('Categories', error)
    if ((error as BackendError).notFound) {
      return {
        notFound: true,
        revalidate: REVALIDATION_ERROR_TIMEOUT,
      }
    }
    throw error
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await strapi.faqCategories.listSlugs()

  return {
    fallback: 'blocking',
    paths: slugs.map((slug) => ({
      params: {
        slug: String(slug),
      },
    })),
  }
}
